<template>
  <div class="card border-0 rounded-3 mb-4 bg-white shadow-sm">
    <div class="card-header bg-light-subtle d-flex justify-content-between align-items-center py-3 px-4">
      <div class="d-flex align-items-center gap-2">
        <span class="material-symbols-outlined text-success fs-22">verified_user</span>
        <h5 class="mb-0 fs-15 fw-bold text-dark">Deliverables Resmi & Quality Control (QC)</h5>
      </div>
      <div class="d-flex align-items-center gap-2">
        <span class="badge px-3 py-2 fs-12" :class="qcStatusBadgeClass(order.qc_status)">
          QC: {{ order.qc_status || 'PENDING' }}
        </span>
        <div class="d-inline-flex align-items-center gap-1 bg-secondary-subtle text-secondary px-3 py-1 rounded-2 fs-12">
          <span>Run {{ order.current_revision_run || 1 }} / {{ order.max_revision_runs || 1 }}</span>
          <button 
            type="button" 
            class="btn btn-xs btn-link p-0 text-primary text-decoration-none d-flex align-items-center ms-1"
            @click="openEditMaxRunsPrompt"
            title="Ubah Kuota Batas Maksimal Revisi Pelanggan"
          >
            <span class="material-symbols-outlined fs-15">edit</span>
          </button>
        </div>
      </div>
    </div>

    <div class="card-body p-4">
      <!-- 3 Official Deliverable Files Status -->
      <h6 class="fs-14 fw-bold text-dark mb-3">3 Berkas Luaran Resmi (Deliverables) Sesuai SOP RBN</h6>
      <div class="row g-3 mb-4">
        <!-- File 1: Naskah DOCX -->
        <div class="col-md-4">
          <div class="p-3 border rounded-3 h-100 bg-light-subtle d-flex flex-column justify-content-between">
            <div>
              <div class="d-flex align-items-center gap-2 mb-2">
                <span class="material-symbols-outlined text-primary fs-20">description</span>
                <span class="fw-bold fs-13 text-dark">1. Naskah Hasil (.docx)</span>
              </div>
              <p class="text-muted fs-12 mb-2">Naskah hasil perbaikan / parafrase final.</p>
              <div v-if="order.final_manuscript_docx" class="text-success fs-12 fw-medium text-truncate mb-2" :title="getFilename(order.final_manuscript_docx)">
                <span class="material-symbols-outlined fs-14 align-middle">check_circle</span>
                {{ getFilename(order.final_manuscript_docx) }}
              </div>
              <div v-else class="text-muted fs-12 mb-2 italic">Belum diunggah</div>
            </div>
            
            <div class="mt-2 pt-2 border-top d-flex gap-2">
              <a v-if="order.final_manuscript_docx" :href="getFileUrl(order.final_manuscript_docx)" target="_blank" class="btn btn-xs btn-outline-primary btn-sm fs-11">
                Unduh
              </a>
              <label class="btn btn-xs btn-primary btn-sm fs-11 mb-0 cursor-pointer">
                Unggah DOCX
                <input type="file" class="d-none" accept=".docx,.doc" @change="uploadDocx" :disabled="uploadingDocx" />
              </label>
            </div>
          </div>
        </div>

        <!-- File 2: Raw Vendor Files (PDF, ZIP, TAR.GZ) -->
        <div class="col-md-4">
          <div class="p-3 border rounded-3 h-100 bg-light-subtle d-flex flex-column justify-content-between">
            <div>
              <div class="d-flex align-items-center justify-content-between mb-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="material-symbols-outlined text-danger fs-20">inventory_2</span>
                  <span class="fw-bold fs-13 text-dark">2. File Asli Software (.pdf, .zip, .tar.gz)</span>
                </div>
                <span class="badge bg-danger-subtle text-danger fs-11" v-if="vendorPdfList.length > 0">
                  {{ vendorPdfList.length }} File
                </span>
              </div>
              <p class="text-muted fs-12 mb-2">Laporan asli Turnitin, Grammarly, AI detector, berkas arsip (.zip, .tar.gz), dll.</p>
              
              <!-- File List -->
              <div v-if="vendorPdfList.length > 0" class="mb-2" style="max-height: 180px; overflow-y: auto;">
                <div 
                  v-for="(file, idx) in vendorPdfList" 
                  :key="idx" 
                  class="d-flex align-items-center justify-content-between py-1 px-2 border rounded bg-white mb-1 shadow-xs"
                >
                  <div class="d-flex align-items-center gap-1 text-truncate me-2" :title="file.name || getFilename(file.path)">
                    <span class="material-symbols-outlined fs-16 flex-shrink-0" :class="getVendorFileIconClass(file)">
                      {{ getVendorFileIcon(file) }}
                    </span>
                    <span class="fs-12 text-dark text-truncate">{{ file.name || getFilename(file.path) }}</span>
                  </div>
                  <div class="d-flex align-items-center gap-1 flex-shrink-0">
                    <a :href="getFileUrl(file.path)" target="_blank" :download="file.name || getFilename(file.path)" class="btn btn-xs btn-outline-danger p-1 lh-1" :title="'Unduh ' + (file.name || getFilename(file.path))">
                      <span class="material-symbols-outlined fs-14">download</span>
                    </a>
                    <button type="button" @click="deleteVendorPdf(idx)" class="btn btn-xs btn-outline-secondary p-1 lh-1 text-danger" title="Hapus Berkas">
                      <span class="material-symbols-outlined fs-14">delete</span>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted fs-12 mb-2 italic">Belum diunggah</div>
            </div>
            
            <div class="mt-2 pt-2 border-top d-flex gap-2">
              <label class="btn btn-xs btn-danger btn-sm fs-11 mb-0 cursor-pointer d-inline-flex align-items-center gap-1">
                <span v-if="uploadingPdf" class="spinner-border spinner-border-sm"></span>
                <span class="material-symbols-outlined fs-14">upload_file</span>
                <span>{{ vendorPdfList.length > 0 ? '+ Tambah Berkas' : 'Unggah Berkas Asli' }}</span>
                <input type="file" class="d-none" accept=".pdf,.zip,.tar.gz,.gz,.tar,.rar,.7z" multiple @change="uploadVendorPdfs" :disabled="uploadingPdf" />
              </label>
            </div>
          </div>
        </div>

        <!-- File 3: Generated Protocol PDFs (Per Protocol Output) -->
        <div class="col-md-4">
          <div class="p-3 border rounded-3 h-100 bg-light-subtle d-flex flex-column justify-content-between">
            <div>
              <div class="d-flex align-items-center justify-content-between mb-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="material-symbols-outlined text-success fs-20">verified</span>
                  <span class="fw-bold fs-13 text-dark">3. Protokol Resmi RBN A4</span>
                </div>
                <span class="badge bg-success-subtle text-success fs-11" v-if="protocolsList.length > 0">
                  {{ protocolsList.length }} Protokol
                </span>
                <span class="badge bg-secondary-subtle text-secondary fs-11" v-else-if="!isProtocolRequired">
                  Tidak Ada Protokol
                </span>
              </div>
              <p class="text-muted fs-12 mb-2" v-if="isProtocolRequired">Sertifikat protokol ber-QR Code resmi auto-generate.</p>
              <p class="text-muted fs-12 mb-2" v-else>Layanan/paket ini tidak mewajibkan sertifikat protokol SOP.</p>
              
              <!-- If protocol is required -->
              <template v-if="isProtocolRequired">
                <!-- Multi-protocol list -->
                <div v-if="protocolsList.length > 0" class="mb-2" style="max-height: 220px; overflow-y: auto;">
                  <div 
                    v-for="(proto, idx) in protocolsList" 
                    :key="idx" 
                    class="p-2 border rounded bg-white mb-2 shadow-xs"
                  >
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <span class="fw-bold fs-12 text-dark text-truncate me-1">{{ proto.service_title || proto.protocol_type }}</span>
                      <span class="badge" :class="proto.status === 'VALID' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'" style="font-size: 10px;">
                        {{ proto.status }}
                      </span>
                    </div>
                    <div class="fs-11 text-muted text-truncate mb-1">
                      No: <strong>{{ proto.protocol_number || '-' }}</strong>
                    </div>
                    <div v-if="proto.author_name || proto.document_title" class="fs-11 text-dark mb-2 bg-light p-1 rounded border border-light-subtle">
                      <div v-if="proto.author_name" class="text-truncate">
                        <span class="text-muted">Pengguna:</span> <strong>{{ proto.author_name }}</strong>
                      </div>
                      <div v-if="proto.document_title" class="text-truncate" :title="proto.document_title">
                        <span class="text-muted">Judul:</span> <em>"{{ proto.document_title }}"</em>
                      </div>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <a v-if="proto.pdf_path" :href="getFileUrl(proto.pdf_path)" target="_blank" class="btn btn-xs btn-success btn-sm fs-11 py-1 px-2 d-inline-flex align-items-center gap-1">
                        <span class="material-symbols-outlined fs-13">open_in_new</span>
                        <span>Buka</span>
                      </a>
                      <button 
                        @click="generateSpecificProtocolPdf(proto.protocol_type)" 
                        :disabled="generatingProto === proto.protocol_type || generating" 
                        class="btn btn-xs btn-outline-success btn-sm fs-11 py-1 px-2 d-inline-flex align-items-center gap-1"
                      >
                        <span v-if="generatingProto === proto.protocol_type" class="spinner-border spinner-border-sm"></span>
                        <span>{{ proto.pdf_path ? 'Regenerate' : 'Generate' }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Fallback single protocol view -->
                <div v-else-if="order.generated_protocol_pdf" class="mb-2">
                  <div class="text-success fs-12 fw-medium text-truncate mb-2" :title="getFilename(order.generated_protocol_pdf)">
                    <span class="material-symbols-outlined fs-14 align-middle">check_circle</span>
                    {{ getFilename(order.generated_protocol_pdf) }}
                  </div>
                </div>
                <div v-else class="text-muted fs-12 mb-2 italic">Otomatis saat simpan form protokol</div>
              </template>

              <!-- If protocol is NOT required -->
              <template v-else>
                <div class="p-3 border rounded bg-light text-muted fs-12 d-flex align-items-center gap-2">
                  <span class="material-symbols-outlined fs-18 text-secondary">info</span>
                  <span>Tidak ada lembar protokol resmi untuk jenis layanan/paket ini.</span>
                </div>
              </template>
            </div>

            <div v-if="isProtocolRequired" class="mt-2 pt-2 border-top d-flex gap-2 flex-wrap">
              <a v-if="protocolsList.length === 0 && order.generated_protocol_pdf" :href="getFileUrl(order.generated_protocol_pdf)" target="_blank" class="btn btn-xs btn-success btn-sm fs-11">
                Buka Dokumen
              </a>
              <button @click="generateAllProtocolsPdf" :disabled="generating" class="btn btn-xs btn-outline-success btn-sm fs-11 d-inline-flex align-items-center gap-1">
                <span v-if="generating" class="spinner-border spinner-border-sm"></span>
                <span class="material-symbols-outlined fs-13">refresh</span>
                <span>{{ protocolsList.length > 1 ? 'Generate Semua' : 'Generate PDF' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Quality Control Actions Section -->
      <div class="p-3 rounded-3 bg-light border mb-4">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 class="fs-13 fw-bold text-dark mb-0 d-flex align-items-center gap-2">
            <span class="material-symbols-outlined text-primary fs-18">rule</span>
            <span>Evaluasi & Keputusan Quality Control (QC)</span>
          </h6>
          <div class="text-muted fs-12 d-flex align-items-center flex-wrap gap-2">
            <span>
              Status Izin Revisi Pelanggan: 
              <strong :class="order.revision_allowed ? 'text-success' : 'text-danger'">
                {{ order.revision_allowed ? 'DIBUKA' : 'DITUTUP' }}
              </strong>
              (Tersisa {{ Math.max(0, (order.max_revision_runs || 1) - (order.current_revision_run || 1)) }} kesempatan dari total {{ order.max_revision_runs || 1 }} Run)
            </span>
            <button 
              type="button" 
              class="btn btn-xs btn-outline-primary py-0 px-2 fs-11 d-inline-flex align-items-center gap-1"
              @click="addRevisionRunQuota"
              :disabled="updatingQc"
              title="Tambah kuota batas revisi pelanggan sebanyak 1 run"
            >
              <span class="material-symbols-outlined fs-14">add_circle</span>
              <span>Buka Limit (+1 Run)</span>
            </button>
          </div>
        </div>

        <!-- Destination Selector (Tujuan QC) -->
        <div class="p-2 mb-3 bg-white rounded border">
          <label class="form-label fs-12 fw-semibold text-secondary mb-1">Tujuan Evaluasi QC:</label>
          <div class="d-flex gap-4">
            <div class="form-check">
              <input class="form-check-input" type="radio" name="qcTarget" id="targetEditor" value="editor" v-model="qcTarget">
              <label class="form-check-label fs-13 text-dark cursor-pointer" for="targetEditor">
                <strong>Ke Editor</strong> <span class="text-muted">(Internal - perbaikan pengerjaan/protokol)</span>
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" name="qcTarget" id="targetPelanggan" value="pelanggan" v-model="qcTarget">
              <label class="form-check-label fs-13 text-dark cursor-pointer" for="targetPelanggan">
                <strong>Ke Pelanggan</strong> <span class="text-muted">(Eksternal - kembalikan untuk revisi / tanggapan)</span>
              </label>
            </div>
          </div>
        </div>
        
        <div class="row g-3 mb-3">
          <div class="col-md-8">
            <label class="form-label fs-12 fw-semibold text-secondary">Catatan Evaluasi / Instruksi QC</label>
            <textarea class="form-control form-control-sm fs-13" rows="3" v-model="qcNotes" placeholder="Tuliskan catatan hasil review QC untuk editor atau instruksi perbaikan untuk pelanggan..."></textarea>
          </div>
          <div class="col-md-4">
            <label class="form-label fs-12 fw-semibold text-secondary">Lampiran Berkas QC (Opsional)</label>
            <input type="file" class="form-control form-control-sm fs-12" @change="onQcAttachmentChange" />
            <div v-if="qcAttachment" class="text-primary fs-11 mt-1">
              File terpilih: {{ qcAttachment.name }}
            </div>
            <small class="text-muted fs-11 d-block mt-1">Sertakan berkas markup, anotasi review, atau referensi jika ada.</small>
          </div>
        </div>

        <!-- Alert Kuota Revisi Habis dengan Tombol Buka Limit Langsung -->
        <div 
          v-if="qcTarget === 'pelanggan' && (order.current_revision_run || 1) >= (order.max_revision_runs || 1)" 
          class="alert alert-warning py-2 px-3 fs-12 mb-3 rounded-2 d-flex justify-content-between align-items-center flex-wrap gap-2"
        >
          <div class="d-flex align-items-center gap-2">
            <span class="material-symbols-outlined text-warning-emphasis fs-18">lock_clock</span>
            <span class="text-dark">Batas kuota revisi pelanggan telah mencapai batas maksimum ({{ order.current_revision_run || 1 }} dari {{ order.max_revision_runs || 1 }} Run). Ingin membuka limit revisi tambahan untuk pelanggan?</span>
          </div>
          <button 
            type="button" 
            class="btn btn-sm btn-primary py-1 px-3 fs-12 d-inline-flex align-items-center gap-1" 
            @click="addRevisionRunQuota"
            :disabled="updatingQc"
          >
            <span class="material-symbols-outlined fs-16">add_circle</span>
            <span>Buka Limit (+1 Run Revisi)</span>
          </button>
        </div>

        <div class="pt-2 border-top">
          <!-- Syarat Approve QC Checklist (ditampilkan di atas tombol agar tombol sejajar rapi) -->
          <div v-if="!canApproveQc" class="d-flex justify-content-end align-items-center gap-2 mb-2 fs-11 text-muted">
            <span class="fw-medium text-secondary">Syarat Approve QC:</span>
            <span :class="hasResultDocument ? 'text-success fw-medium' : 'text-danger'" class="d-inline-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-14">{{ hasResultDocument ? 'check_circle' : 'cancel' }}</span>
              <span>1 Dokumen Hasil</span>
            </span>
            <template v-if="isProtocolRequired">
              <span>•</span>
              <span :class="hasGeneratedProtocol ? 'text-success fw-medium' : 'text-danger'" class="d-inline-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-14">{{ hasGeneratedProtocol ? 'check_circle' : 'cancel' }}</span>
                <span>Generate Protokol</span>
              </span>
            </template>
          </div>

          <div class="d-flex justify-content-end align-items-center flex-wrap gap-2">
            <!-- Button: Kembalikan ke Editor (aktif jika target editor) -->
            <button 
              v-if="qcTarget === 'editor'"
              @click="updateQc('RETURN_TO_EDITOR', false, undefined, 'editor', 'QC')" 
              :disabled="updatingQc" 
              class="btn btn-sm btn-outline-warning d-flex align-items-center gap-1"
            >
              <span class="material-symbols-outlined fs-16">undo</span>
              <span>Kembalikan ke Editor</span>
            </button>

            <!-- Button: Kembalikan ke Pelanggan (aktif jika target pelanggan) -->
            <button 
              v-if="qcTarget === 'pelanggan'"
              @click="updateQc('REVISE_BY_USER', true, undefined, 'pelanggan', 'QC')" 
              :disabled="updatingQc || (order.current_revision_run || 1) >= (order.max_revision_runs || 1)" 
              class="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
              :title="(order.current_revision_run || 1) >= (order.max_revision_runs || 1) ? 'Batas maksimal kuota revisi pelanggan telah tercapai' : ''"
            >
              <span class="material-symbols-outlined fs-16">undo</span>
              <span>Kembalikan ke Pelanggan</span>
            </button>

            <!-- Button: Approve QC & Selesaikan Pesanan -->
            <button 
              @click="updateQc('APPROVED', false, 'Completed', 'editor', 'QC')" 
              :disabled="updatingQc || !canApproveQc" 
              class="btn btn-sm btn-success d-flex align-items-center gap-1"
              :title="!canApproveQc ? (isProtocolRequired ? 'Lengkapi minimal 1 dokumen hasil dan generate protokol untuk Approve QC' : 'Unggah minimal 1 dokumen hasil untuk Approve QC') : 'Approve QC dan Selesaikan Pesanan'"
            >
              <span class="material-symbols-outlined fs-16">check_circle</span>
              <span>Approve QC & Selesaikan Pesanan</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Editor Reply Section (if Order is currently in RETURN_TO_EDITOR) -->
      <div v-if="order.qc_status === 'RETURN_TO_EDITOR'" class="p-3 rounded-3 bg-warning-subtle border border-warning mb-4">
        <div class="d-flex align-items-center gap-2 mb-2">
          <span class="material-symbols-outlined text-warning-emphasis fs-20">assignment_return</span>
          <h6 class="fs-13 fw-bold text-warning-emphasis mb-0">Tanggapan Editor ke QC (Perbaikan Naskah / Protokol Selesai)</h6>
        </div>
        <p class="fs-12 text-muted mb-2">
          Pesanan ini dikembalikan oleh QC untuk dilakukan perbaikan. Setelah selesai memperbarui protokol atau naskah, silakan kirimkan tanggapan di bawah ini untuk diajukan kembali ke QC. (Menyimpan protokol di tab bawah juga akan otomatis mencatat tanggapan editor).
        </p>

        <div class="row g-2 mb-2">
          <div class="col-md-8">
            <input type="text" class="form-control form-control-sm fs-13" v-model="editorReplyNotes" placeholder="Tuliskan keterangan perbaikan yang telah dilakukan..." />
          </div>
          <div class="col-md-4">
            <input type="file" class="form-control form-control-sm fs-12" @change="onEditorAttachmentChange" />
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <button @click="submitEditorReply" :disabled="replyingQc" class="btn btn-sm btn-warning d-flex align-items-center gap-1 text-dark fw-medium">
            <span v-if="replyingQc" class="spinner-border spinner-border-sm me-1"></span>
            <span class="material-symbols-outlined fs-16">send</span>
            <span>Kirim Tanggapan & Ajukan Kembali ke QC</span>
          </button>
        </div>
      </div>

      <!-- Tabel Riwayat & Audit Trail QC -->
      <div class="border rounded-3 overflow-hidden">
        <div class="bg-light-subtle py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center gap-2">
            <span class="material-symbols-outlined text-secondary fs-18">history</span>
            <span class="fw-bold fs-13 text-dark">Riwayat & Log Interaksi QC (Audit Trail)</span>
          </div>
          <span class="badge bg-secondary-subtle text-secondary fs-11">
            {{ (order.qc_histories && order.qc_histories.length) || 0 }} Aktivitas
          </span>
        </div>

        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0 fs-13">
            <thead class="table-light">
              <tr>
                <th scope="col" style="width: 15%;">Waktu</th>
                <th scope="col" style="width: 15%;">Pengirim</th>
                <th scope="col" style="width: 12%;">Tujuan</th>
                <th scope="col" style="width: 15%;">Status & Run</th>
                <th scope="col" style="width: 25%;">Catatan / Pesan</th>
                <th scope="col" style="width: 18%;">Berkas & Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in (order.qc_histories || [])" :key="item.id">
                <td class="text-muted text-nowrap fs-12">
                  {{ formatDateTime(item.created_at) }}
                </td>
                <td>
                  <span class="badge" :class="senderBadgeClass(item.sender_type)">
                    {{ item.sender_type }}
                  </span>
                  <div class="fs-12 text-dark fw-medium mt-1">
                    {{ item.user?.name || item.customer?.name || (item.sender_type === 'PELANGGAN' ? order.customer?.name : 'User') }}
                  </div>
                </td>
                <td>
                  <span class="badge" :class="targetBadgeClass(item.target)">
                    Ke {{ formatTarget(item.target) }}
                  </span>
                </td>
                <td>
                  <span class="badge px-2 py-1 fs-11 mb-1 d-inline-block" :class="qcStatusBadgeClass(item.qc_status)">
                    {{ item.qc_status || '-' }}
                  </span>
                  <div class="fs-11 text-muted">
                    Run ke-{{ item.revision_run || 1 }}
                  </div>
                </td>
                <td>
                  <div class="text-break fs-12 text-dark">
                    {{ item.notes || '-' }}
                  </div>
                </td>
                <td>
                  <div v-if="item.attachment_file" class="d-flex flex-column gap-1">
                    <a :href="getFileUrl(item.attachment_file)" target="_blank" class="btn btn-xs btn-outline-secondary d-inline-flex align-items-center gap-1 text-truncate" style="max-width: 200px;" :title="getFilename(item.attachment_file)">
                      <span class="material-symbols-outlined fs-14">attach_file</span>
                      <span class="text-truncate">{{ getFilename(item.attachment_file) }}</span>
                    </a>

                    <!-- Action: Jadikan File Utama untuk Diedit -->
                    <button 
                      class="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 fw-medium" 
                      @click="setPrimaryFile(item.attachment_file)" 
                      :disabled="settingPrimary"
                      title="Gantikan naskah utama pesanan dengan berkas ini"
                    >
                      <span class="material-symbols-outlined fs-14">swap_horizontal_circle</span>
                      <span>Jadikan File Utama untuk Diedit</span>
                    </button>
                  </div>
                  <span v-else class="text-muted fs-12">-</span>
                </td>
              </tr>
              <tr v-if="!order.qc_histories || order.qc_histories.length === 0">
                <td colspan="6" class="text-center py-4 text-muted fs-12">
                  <span class="material-symbols-outlined fs-24 d-block mb-1 text-muted">receipt_long</span>
                  Belum ada catatan riwayat QC pada pesanan ini.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue';
import api from '../../../services/api';
import uploadService from '../../../services/uploadService';
import orderService from '../../../services/orderService';

export default defineComponent({
  name: 'OrderDeliverablesAndQc',
  props: {
    order: {
      type: Object,
      required: true
    },
    requiredProtocols: {
      type: Array as () => any[],
      default: null
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const qcNotes = ref(props.order.qc_notes || '');
    const qcTarget = ref(props.order.qc_target || 'editor');
    const qcAttachment = ref<File | null>(null);

    const editorReplyNotes = ref('');
    const editorAttachment = ref<File | null>(null);

    const uploadingDocx = ref(false);
    const uploadingPdf = ref(false);
    const generating = ref(false);
    const updatingQc = ref(false);
    const replyingQc = ref(false);
    const settingPrimary = ref(false);

    watch(() => props.order, (newOrder) => {
      if (newOrder) {
        qcNotes.value = newOrder.qc_notes || '';
        qcTarget.value = newOrder.qc_target || 'editor';
      }
    }, { deep: true });

    const qcStatusBadgeClass = (status: string) => {
      switch (status) {
        case 'APPROVED': return 'bg-success text-white';
        case 'SUBMITTED_TO_QC': return 'bg-info text-white';
        case 'RETURN_TO_EDITOR': return 'bg-warning text-dark';
        case 'REVISE_BY_USER': return 'bg-danger text-white';
        default: return 'bg-secondary-subtle text-secondary';
      }
    };

    const senderBadgeClass = (sender: string) => {
      switch (sender) {
        case 'QC': return 'bg-primary text-white';
        case 'EDITOR': return 'bg-warning-subtle text-warning-emphasis border border-warning';
        case 'PELANGGAN': return 'bg-info-subtle text-info-emphasis border border-info';
        default: return 'bg-light text-dark border';
      }
    };

    const targetBadgeClass = (target: string) => {
      switch (target) {
        case 'editor': return 'bg-warning-subtle text-dark';
        case 'pelanggan': return 'bg-danger-subtle text-danger';
        case 'qc': return 'bg-success-subtle text-success';
        default: return 'bg-light text-secondary border';
      }
    };

    const formatTarget = (target: string) => {
      if (target === 'editor') return 'Editor';
      if (target === 'pelanggan') return 'Pelanggan';
      if (target === 'qc') return 'QC';
      return target || '-';
    };

    const formatDateTime = (dateStr: string) => {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getFilename = (path: string) => {
      if (!path) return '';
      return path.split('/').pop()?.split('\\').pop() || path;
    };

    const getFileUrl = (path: string) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${path}`;
    };

    const onQcAttachmentChange = (e: any) => {
      if (e.target.files && e.target.files[0]) {
        qcAttachment.value = e.target.files[0];
      }
    };

    const onEditorAttachmentChange = (e: any) => {
      if (e.target.files && e.target.files[0]) {
        editorAttachment.value = e.target.files[0];
      }
    };

    const uploadDocx = async (e: any) => {
      if (!e.target.files?.[0]) return;
      uploadingDocx.value = true;
      try {
        const res = await uploadService.uploadFile(e.target.files[0]);
        await orderService.update(props.order.order_id, {
          final_manuscript_docx: res.data.path
        });
        alert('Naskah DOCX berhasil diunggah.');
        emit('refresh');
      } catch (err) {
        console.error('Failed to upload docx:', err);
        alert('Gagal mengunggah naskah DOCX.');
      } finally {
        uploadingDocx.value = false;
      }
    };

    const generatingProto = ref<string | null>(null);

    const vendorPdfList = computed(() => {
      const o = props.order;
      let list: Array<{ name: string; path: string }> = [];

      if (o.raw_vendor_pdfs_list && Array.isArray(o.raw_vendor_pdfs_list) && o.raw_vendor_pdfs_list.length > 0) {
        list = [...o.raw_vendor_pdfs_list];
      } else if (o.raw_vendor_pdfs && Array.isArray(o.raw_vendor_pdfs) && o.raw_vendor_pdfs.length > 0) {
        list = o.raw_vendor_pdfs.map((item: any) => {
          if (typeof item === 'string') return { name: getFilename(item), path: item };
          return { name: item.name || getFilename(item.path), path: item.path };
        });
      }

      // Jika sebelumnya sudah terlanjur ada attachment (single raw_vendor_pdf),
      // pastikan selalu tampil sebagai lampiran pertama di daftar
      if (o.raw_vendor_pdf) {
        const existingIdx = list.findIndex(item => item.path === o.raw_vendor_pdf);
        if (existingIdx === -1) {
          list.unshift({ name: getFilename(o.raw_vendor_pdf), path: o.raw_vendor_pdf });
        } else if (existingIdx > 0) {
          const [found] = list.splice(existingIdx, 1);
          list.unshift(found);
        }
      }

      return list;
    });

    const isArchiveFile = (pathOrName: string) => {
      const lower = (pathOrName || '').toLowerCase();
      return lower.endsWith('.zip') || lower.endsWith('.tar.gz') || lower.endsWith('.gz') || lower.endsWith('.tar') || lower.endsWith('.rar') || lower.endsWith('.7z');
    };

    const getVendorFileIcon = (file: { name?: string; path: string }) => {
      const filename = file.name || file.path;
      if (isArchiveFile(filename)) return 'folder_zip';
      if ((filename || '').toLowerCase().endsWith('.pdf')) return 'picture_as_pdf';
      return 'description';
    };

    const getVendorFileIconClass = (file: { name?: string; path: string }) => {
      const filename = file.name || file.path;
      if (isArchiveFile(filename)) return 'text-warning';
      if ((filename || '').toLowerCase().endsWith('.pdf')) return 'text-danger';
      return 'text-primary';
    };

    const protocolsList = computed(() => {
      return props.order.official_protocols_list || [];
    });

    const uploadVendorPdfs = async (e: any) => {
      const files: FileList = e.target.files;
      if (!files || files.length === 0) return;
      
      uploadingPdf.value = true;
      try {
        const currentList = [...vendorPdfList.value];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const res = await uploadService.uploadFile(file);
          currentList.push({
            name: res.data.name || file.name,
            path: res.data.path
          });
        }
        await orderService.update(props.order.order_id, {
          raw_vendor_pdfs: currentList
        });
        alert(`${files.length} Berkas asli software berhasil diunggah.`);
        emit('refresh');
      } catch (err) {
        console.error('Failed to upload vendor files:', err);
        alert('Gagal mengunggah berkas asli software.');
      } finally {
        uploadingPdf.value = false;
        if (e.target) e.target.value = '';
      }
    };

    const deleteVendorPdf = async (index: number) => {
      if (!confirm('Hapus berkas software ini dari daftar?')) return;
      const currentList = [...vendorPdfList.value];
      currentList.splice(index, 1);
      try {
        await orderService.update(props.order.order_id, {
          raw_vendor_pdfs: currentList
        });
        alert('Berkas software berhasil dihapus.');
        emit('refresh');
      } catch (err) {
        console.error('Failed to delete vendor file:', err);
        alert('Gagal menghapus berkas software.');
      }
    };

    const generateSpecificProtocolPdf = async (protocolType: string) => {
      generatingProto.value = protocolType;
      try {
        await api.post(`/orders/${props.order.order_id}/generate-protocol-pdf`, {
          protocol_type: protocolType
        });
        alert(`PDF Protokol ${protocolType} berhasil digenerate.`);
        emit('refresh');
      } catch (err: any) {
        console.error('Failed to generate protocol pdf:', err);
        alert(err.response?.data?.message || 'Gagal generate PDF protokol.');
      } finally {
        generatingProto.value = null;
      }
    };

    const generateAllProtocolsPdf = async () => {
      generating.value = true;
      try {
        await api.post(`/orders/${props.order.order_id}/generate-protocol-pdf`);
        alert('Semua berkas protokol resmi berhasil digenerate.');
        emit('refresh');
      } catch (err: any) {
        console.error('Failed to generate protocol pdf:', err);
        alert(err.response?.data?.message || 'Gagal generate PDF protokol.');
      } finally {
        generating.value = false;
      }
    };

    const hasResultDocument = computed(() => {
      const o = props.order;
      const hasVendorPdfs = vendorPdfList.value.length > 0;
      return !!(
        o.final_manuscript_docx ||
        hasVendorPdfs ||
        o.raw_vendor_pdf ||
        o.plagiarism_detail?.result_report_file ||
        o.ai_check_detail?.result_report_file ||
        o.paraphrase_detail?.result_document_file ||
        o.document_fix_detail?.result_document_file ||
        o.journal_detail?.result_file ||
        o.translate_detail?.result_document_file ||
        o.all_detail?.loi_certificate_file
      );
    });

    const isProtocolRequired = computed(() => {
      if (props.requiredProtocols !== null && Array.isArray(props.requiredProtocols)) {
        return props.requiredProtocols.length > 0;
      }
      if (typeof props.order?.requires_protocol === 'boolean') {
        return props.order.requires_protocol;
      }
      const o = props.order;
      if (!o) return false;
      const protocolTypes = ['PLAGIARISM', 'PLAGIARISM_AI_CHECK', 'PARAPHRASE', 'AI_CHECK', 'ALL'];
      if (o.service_type && protocolTypes.includes(o.service_type)) return true;
      if (o.package?.services && Array.isArray(o.package.services)) {
        return o.package.services.some((s: any) => protocolTypes.includes(s.service_type));
      }
      return false;
    });

    const hasGeneratedProtocol = computed(() => {
      const o = props.order;
      const hasProtoInList = protocolsList.value.length > 0 && protocolsList.value.some((p: any) => !!p.pdf_path);
      return !!(
        hasProtoInList ||
        o.generated_protocol_pdf ||
        (Array.isArray(o.generated_protocol_pdfs) && o.generated_protocol_pdfs.length > 0) ||
        o.verification_id ||
        o.protocol_id ||
        o.protocol_similarity ||
        o.protocol_grammarly ||
        o.protocol_ai_detector ||
        o.protocol_plagiarism_ai
      );
    });

    const canApproveQc = computed(() => {
      if (isProtocolRequired.value) {
        return hasResultDocument.value && hasGeneratedProtocol.value;
      }
      return hasResultDocument.value;
    });

    const updateQc = async (
      status: string,
      allowRevision: boolean,
      orderStatus?: string,
      target = 'editor',
      senderType = 'QC'
    ) => {
      if (status === 'APPROVED' || orderStatus === 'Completed') {
        if (!hasResultDocument.value) {
          alert('Syarat belum terpenuhi: Setidaknya 1 dokumen hasil (Naskah DOCX atau Laporan Asli Software) harus diunggah terlebih dahulu sebelum Approve QC / menyelesaikan pesanan.');
          return;
        }
        if (isProtocolRequired.value && !hasGeneratedProtocol.value) {
          alert('Syarat belum terpenuhi: Protokol resmi harus digenerate setidaknya satu kali terlebih dahulu sebelum Approve QC / menyelesaikan pesanan.');
          return;
        }
      }

      updatingQc.value = true;
      try {
        let attachmentPath: string | null = null;
        if (qcAttachment.value) {
          const res = await uploadService.uploadFile(qcAttachment.value);
          attachmentPath = res.data.path;
        }

        const payload: any = {
          qc_status: status,
          revision_allowed: allowRevision,
          qc_notes: qcNotes.value,
          qc_target: target,
          target: target,
          sender_type: senderType,
        };

        if (attachmentPath) {
          payload.attachment_file = attachmentPath;
        }

        if (orderStatus) {
          payload.order_status = orderStatus;
        }

        await orderService.update(props.order.order_id, payload);
        alert(`Status QC berhasil diubah menjadi ${status}`);
        qcAttachment.value = null;
        emit('refresh');
      } catch (err: any) {
        console.error('Failed to update QC status:', err);
        alert(err.response?.data?.message || 'Gagal memperbarui status QC.');
      } finally {
        updatingQc.value = false;
      }
    };

    const submitEditorReply = async () => {
      if (!editorReplyNotes.value.trim() && !editorAttachment.value) {
        alert('Silakan tuliskan keterangan perbaikan atau lampirkan berkas perbaikan.');
        return;
      }

      replyingQc.value = true;
      try {
        let attachmentPath: string | null = null;
        if (editorAttachment.value) {
          const res = await uploadService.uploadFile(editorAttachment.value);
          attachmentPath = res.data.path;
        }

        await orderService.update(props.order.order_id, {
          qc_status: 'SUBMITTED_TO_QC',
          sender_type: 'EDITOR',
          target: 'qc',
          qc_notes: editorReplyNotes.value,
          attachment_file: attachmentPath
        });

        alert('Tanggapan berhasil dikirim dan diajukan kembali ke QC.');
        editorReplyNotes.value = '';
        editorAttachment.value = null;
        emit('refresh');
      } catch (err: any) {
        console.error('Failed to submit editor reply:', err);
        alert(err.response?.data?.message || 'Gagal mengirimkan tanggapan ke QC.');
      } finally {
        replyingQc.value = false;
      }
    };

    const setPrimaryFile = async (filePath: string) => {
      if (!confirm(`Gantikan berkas sumber naskah order dengan berkas "${getFilename(filePath)}"? Tindakan ini akan menjadikan berkas ini sebagai naskah aktif untuk diproses oleh editor.`)) {
        return;
      }

      settingPrimary.value = true;
      try {
        await api.post(`/orders/${props.order.order_id}/set-primary-file`, {
          file_path: filePath
        });
        alert('Berkas revisi pelanggan berhasil ditetapkan sebagai berkas naskah utama pesanan.');
        emit('refresh');
      } catch (err: any) {
        console.error('Failed to set primary file:', err);
        alert(err.response?.data?.message || 'Gagal menetapkan berkas utama.');
      } finally {
        settingPrimary.value = false;
      }
    };

    const addRevisionRunQuota = async () => {
      const currentMax = Number(props.order.max_revision_runs) || 1;
      const newMax = currentMax + 1;
      if (!confirm(`Buka limit revisi pelanggan? Kuota maksimal akan dinaikkan dari ${currentMax} menjadi ${newMax} Run.`)) {
        return;
      }
      updatingQc.value = true;
      try {
        await orderService.update(props.order.order_id, {
          max_revision_runs: newMax
        });
        alert(`Batas maksimal revisi pelanggan berhasil dinaikkan menjadi ${newMax} Run.`);
        emit('refresh');
      } catch (err: any) {
        console.error('Failed to increase max_revision_runs:', err);
        alert(err.response?.data?.message || 'Gagal mengubah limit revisi.');
      } finally {
        updatingQc.value = false;
      }
    };

    const openEditMaxRunsPrompt = async () => {
      const currentMax = Number(props.order.max_revision_runs) || 1;
      const input = prompt('Masukkan batas kuota maksimal revisi (Run) untuk pesanan ini:', String(currentMax));
      if (input === null) return;
      const parsed = parseInt(input.trim(), 10);
      if (isNaN(parsed) || parsed < 1) {
        alert('Jumlah kuota revisi harus berupa angka bulat minimal 1.');
        return;
      }
      if (parsed === currentMax) return;
      updatingQc.value = true;
      try {
        await orderService.update(props.order.order_id, {
          max_revision_runs: parsed
        });
        alert(`Batas maksimal revisi pelanggan berhasil diperbarui menjadi ${parsed} Run.`);
        emit('refresh');
      } catch (err: any) {
        console.error('Failed to update max_revision_runs:', err);
        alert(err.response?.data?.message || 'Gagal mengubah batas maksimal revisi.');
      } finally {
        updatingQc.value = false;
      }
    };

    return {
      qcNotes,
      qcTarget,
      qcAttachment,
      editorReplyNotes,
      editorAttachment,
      uploadingDocx,
      uploadingPdf,
      generating,
      updatingQc,
      replyingQc,
      settingPrimary,
      qcStatusBadgeClass,
      senderBadgeClass,
      targetBadgeClass,
      formatTarget,
      formatDateTime,
      getFilename,
      getFileUrl,
      onQcAttachmentChange,
      onEditorAttachmentChange,
      uploadDocx,
      vendorPdfList,
      protocolsList,
      uploadVendorPdfs,
      deleteVendorPdf,
      generatingProto,
      generateSpecificProtocolPdf,
      generateAllProtocolsPdf,
      updateQc,
      submitEditorReply,
      setPrimaryFile,
      addRevisionRunQuota,
      openEditMaxRunsPrompt,
      hasResultDocument,
      hasGeneratedProtocol,
      isProtocolRequired,
      canApproveQc,
      isArchiveFile,
      getVendorFileIcon,
      getVendorFileIconClass
    };
  }
});
</script>
